import { createEffect, createSignal, Show, untrack } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@fleet/ui";
import { Select } from "@/components/ui/select";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { createPowerSyncQuery } from "@fieldcheck/shared/solid";
import {
  createFindingMutation,
  FINDING_SEVERITIES,
  FINDING_STATUSES,
  type FindingRecord,
  type FindingSeverity,
  type FindingStatus,
  getOrgMembersLocalQuery,
  getSitesQuery,
  isFindingSeverity,
  normalizeFindingStatus,
  nowISO,
  queryKeys,
  type SiteRecord,
  updateFindingMutation,
} from "@fieldcheck/shared";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { PhotoStrip } from "@/features/inspections/components/photo-strip";

export const SEVERITY_LABELS: Record<FindingSeverity, MessageDescriptor> = {
  low: msg`Low`,
  medium: msg`Medium`,
  high: msg`High`,
  critical: msg`Critical`,
};

export const STATUS_LABELS: Record<FindingStatus, MessageDescriptor> = {
  open: msg`Open`,
  in_progress: msg`In Progress`,
  resolved: msg`Resolved`,
};

/** datetime-local <-> ISO: the input uses "YYYY-MM-DDTHH:mm" in local time. */
function isoToDateInput(iso: string | null | undefined): string {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  // Date only (YYYY-MM-DD) — due date is at day granularity.
  const tzOffsetMs = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 10);
}

function dateInputToIso(value: string): string | null {
  if (!value) {
    return null;
  }
  // value is "YYYY-MM-DD" (input date). Interpreted as local noon so
  // timezone offset does not shift day when converting to ISO/UTC.
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }
  const d = new Date(year, month - 1, day, 12, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export interface FindingDialogContext {
  inspectionId?: string | null;
  inspectionAnswerId?: string | null;
  siteId?: string | null;
  organizationId?: string | null;
  defaultTitle?: string | null;
}

interface FindingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing finding (edit mode). */
  finding?: FindingRecord | null;
  /** Creation context (from an inspection item or standalone). */
  context?: FindingDialogContext;
}

type OrgMemberRow = {
  member_id: string;
  user_id: string | null;
  role: string | null;
  name: string | null;
  email: string | null;
};

export function FindingDialog(props: FindingDialogProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();

  const [title, setTitle] = createSignal("");
  const [description, setDescription] = createSignal("");
  const [severity, setSeverity] = createSignal<FindingSeverity>("medium");
  const [assigneeId, setAssigneeId] = createSignal<string | null>(null);
  const [siteId, setSiteId] = createSignal<string | null>(null);
  const [status, setStatus] = createSignal<FindingStatus>("open");
  const [dueDate, setDueDate] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [createdId, setCreatedId] = createSignal<string | null>(null);

  const organizationId = () =>
    props.finding?.organization_id ?? props.context?.organizationId ?? null;

  // Seed the form ONLY on dialog open transition (CC-16).
  // The previous effect ran on EVERY re-emit while `props.open` was true:
  // `props.context` is a parent memo depending on `answers.data` (a
  // PowerSync query), so answering items — which writes answers — recomputed
  // context → this effect re-ran → `setTitle(defaultTitle)` STOMPED the
  // title typed by the user with the item label (the finding was
  // saved with default title, not typed one). Now seed only once
  // on open; read props with `untrack` to avoid re-subscribing to its
  // reactive dependencies.
  let findingWasOpen = false;
  createEffect(() => {
    const isOpen = props.open;
    if (isOpen && !findingWasOpen) {
      untrack(() => {
        const f = props.finding;
        setTitle(f?.title ?? props.context?.defaultTitle ?? "");
        setDescription(f?.description ?? "");
        setSeverity(isFindingSeverity(f?.severity) ? f.severity : "medium");
        setAssigneeId(f?.assignee_id ?? null);
        setSiteId(f?.site_id ?? props.context?.siteId ?? null);
        setStatus(normalizeFindingStatus(f?.status));
        setDueDate(isoToDateInput(f?.due_date));
        setCreatedId(null);
      });
    }
    findingWasOpen = isOpen;
  });

  const isEditing = () => Boolean(props.finding);
  const isStandalone = () => !props.finding && !props.context?.inspectionId;

  const members = createPowerSyncQuery<OrgMemberRow>({
    queryKey: () => queryKeys.organizations.localMembers(organizationId() ?? undefined),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const orgId = organizationId();
      return dbInstance && orgId ? getOrgMembersLocalQuery(dbInstance, orgId) : null;
    },
    enabled: () => Boolean(drizzle() && organizationId()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const sites = createPowerSyncQuery<SiteRecord>({
    queryKey: () => queryKeys.sites.list(session.userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getSitesQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle()) && isStandalone(),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const dbInstance = drizzle();
    const uid = session.userId();
    if (!title().trim() || !dbInstance || !uid) {
      return;
    }
    setIsSubmitting(true);
    try {
      const statusUpdate = {
        status: status(),
        // Resolving seals the closing date; any other state clears it.
        closed_at: status() === "resolved" ? (props.finding?.closed_at ?? nowISO()) : null,
        due_date: dateInputToIso(dueDate()),
      };
      if (props.finding) {
        await updateFindingMutation(dbInstance, props.finding.id, {
          title: title().trim(),
          description: description().trim() || null,
          severity: severity(),
          assignee_id: assigneeId(),
          site_id: siteId(),
          ...statusUpdate,
        });
        props.onOpenChange(false);
      } else if (createdId()) {
        // Already created (to attach photos): only update and close.
        await updateFindingMutation(dbInstance, createdId() ?? "", {
          title: title().trim(),
          description: description().trim() || null,
          severity: severity(),
          assignee_id: assigneeId(),
          site_id: siteId(),
          ...statusUpdate,
        });
        props.onOpenChange(false);
      } else {
        await createFindingMutation(dbInstance, {
          userId: uid,
          organizationId: organizationId(),
          inspectionId: props.context?.inspectionId ?? null,
          inspectionAnswerId: props.context?.inspectionAnswerId ?? null,
          siteId: siteId(),
          title: title().trim(),
          description: description().trim() || null,
          severity: severity(),
          assigneeId: assigneeId(),
          dueDate: dateInputToIso(dueDate()),
        });
        props.onOpenChange(false);
      }
    } catch (err) {
      console.error("Error saving finding:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /** Creates finding in draft to attach photos before closing dialog. */
  const ensureCreatedForPhotos = async () => {
    const dbInstance = drizzle();
    const uid = session.userId();
    if (!dbInstance || !uid || createdId() || props.finding) {
      return;
    }
    const id = await createFindingMutation(dbInstance, {
      userId: uid,
      organizationId: organizationId(),
      inspectionId: props.context?.inspectionId ?? null,
      inspectionAnswerId: props.context?.inspectionAnswerId ?? null,
      siteId: siteId(),
      title: title().trim() || t(msg`Finding`),
      description: description().trim() || null,
      severity: severity(),
      assigneeId: assigneeId(),
      dueDate: dateInputToIso(dueDate()),
    });
    setCreatedId(id);
  };

  const photoFindingId = () => props.finding?.id ?? createdId();

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-lg p-0">
      <DialogHeader size="md">
        <DialogTitle class="text-xl font-bold">
          {isEditing() ? t(msg`Edit finding`) : t(msg`New finding`)}
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={(e) => void handleSubmit(e)} class="space-y-4 p-6">
        <div class="space-y-2">
          <Label for="finding-title">{t(msg`Title`)}</Label>
          <TextField
            id="finding-title"
            value={title()}
            onInput={(e) => setTitle(e.currentTarget.value)}
            placeholder={t(msg`What is wrong?`)}
            autofocus
          />
        </div>

        <div class="space-y-2">
          <Label for="finding-description">{t(msg`Description`)}</Label>
          <textarea
            id="finding-description"
            value={description()}
            onInput={(e) => setDescription(e.currentTarget.value)}
            rows={3}
            class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            placeholder={t(msg`Details, location, risk...`)}
          />
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="space-y-2">
            <Label for="finding-severity">{t(msg`Severity`)}</Label>
            <Select
              id="finding-severity"
              value={severity()}
              onChange={(v) => {
                if (isFindingSeverity(v)) {
                  setSeverity(v);
                }
              }}
              options={FINDING_SEVERITIES.map((sev) => ({
                value: sev,
                label: t(SEVERITY_LABELS[sev]),
              }))}
            />
          </div>

          <Show when={organizationId()}>
            <div class="space-y-2">
              <Label for="finding-assignee">{t(msg`Assignee`)}</Label>
              <Select
                id="finding-assignee"
                value={assigneeId() ?? ""}
                onChange={(v) => {
                  setAssigneeId(v || null);
                }}
                options={[
                  { value: "", label: t(msg`Unassigned`) },
                  ...(members.data ?? []).map((m) => ({
                    value: m.user_id ?? "",
                    label: m.name ?? m.email ?? m.user_id ?? "",
                  })),
                ]}
              />
            </div>
          </Show>

          <Show when={isStandalone()}>
            <div class="space-y-2">
              <Label for="finding-site">{t(msg`Site`)}</Label>
              <Select
                id="finding-site"
                value={siteId() ?? ""}
                onChange={(v) => {
                  setSiteId(v || null);
                }}
                options={[
                  { value: "", label: t(msg`No site`) },
                  ...(sites.data ?? []).map((s) => ({ value: s.id, label: s.name ?? "" })),
                ]}
              />
            </div>
          </Show>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="space-y-2">
            <Label for="finding-status">{t(msg`Status`)}</Label>
            <Select
              id="finding-status"
              data-testid="finding-status-select"
              value={status()}
              onChange={(v) => {
                setStatus(normalizeFindingStatus(v));
              }}
              options={FINDING_STATUSES.map((st) => ({ value: st, label: t(STATUS_LABELS[st]) }))}
            />
          </div>

          <div class="space-y-2">
            <Label for="finding-due-date">{t(msg`Due date`)}</Label>
            <input
              id="finding-due-date"
              data-testid="finding-due-date"
              type="date"
              value={dueDate()}
              onInput={(e) => setDueDate(e.currentTarget.value)}
              class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            />
          </div>
        </div>

        <Show when={status() === "resolved"}>
          <div class="space-y-2 rounded-lg border border-success/30 bg-success/5 p-3">
            <Label>{t(msg`Closure photo`)}</Label>
            <p class="text-xs text-muted-foreground">
              {t(msg`Attach a photo as evidence the issue was resolved.`)}
            </p>
            <Show
              when={photoFindingId()}
              fallback={
                <Button
                  type="button"
                  variant="outline"
                  class="gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="add-closure-photo"
                  disabled={!drizzle()}
                  onClick={() => void ensureCreatedForPhotos()}
                >
                  {t(msg`Add closure photo...`)}
                </Button>
              }
            >
              {(fid) => (
                <div data-testid="closure-photo-strip">
                  <PhotoStrip
                    parent={{ kind: "finding", findingId: fid(), organizationId: organizationId() }}
                  />
                </div>
              )}
            </Show>
          </div>
        </Show>

        <div class="space-y-2">
          <Label>{t(msg`Photos`)}</Label>
          <Show
            when={photoFindingId()}
            fallback={
              <Button
                type="button"
                variant="outline"
                class="gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={!drizzle()}
                onClick={() => void ensureCreatedForPhotos()}
              >
                {t(msg`Add photos...`)}
              </Button>
            }
          >
            {(fid) => (
              <PhotoStrip
                parent={{ kind: "finding", findingId: fid(), organizationId: organizationId() }}
              />
            )}
          </Show>
        </div>

        <div class="flex items-center justify-end gap-3 pt-4 border-t border-border/10">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              props.onOpenChange(false);
            }}
          >
            {t(msg`Cancel`)}
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting() || !title().trim() || !drizzle()}
            class="disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="save-finding"
          >
            {isEditing() || createdId() ? t(msg`Save changes`) : t(msg`Create finding`)}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
